import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await login(email, password);
      navigate(user.role === "admin" ? "/dashboard" : "/");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="min-h-[calc(100vh-96px)] flex items-center justify-center py-20 px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <h1 className="headline text-4xl sm:text-5xl text-[#1d1d1f] mb-2">{t.auth.loginTitle}</h1>
        <p className="text-[16px] text-[#86868b] mb-10">{t.auth.loginSubtitle}</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">{t.auth.email}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-[#f5f5f7] rounded-2xl px-4 py-3 text-[14px] outline-none"
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">{t.auth.password}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full bg-[#f5f5f7] rounded-2xl px-4 py-3 text-[14px] outline-none"
              data-testid="login-password-input"
            />
          </div>

          {error && <div className="text-[13px] text-red-600" data-testid="login-error">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="w-full btn-pill btn-primary text-[14px] disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {loading ? "…" : t.auth.loginBtn}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-black/10" />
          <span className="text-[12px] text-[#86868b] uppercase tracking-wider">{t.auth.or}</span>
          <div className="flex-1 h-px bg-black/10" />
        </div>

        <button
          onClick={googleLogin}
          className="w-full btn-pill btn-secondary text-[14px] flex items-center justify-center gap-2"
          data-testid="google-login-btn"
        >
          <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          {t.auth.googleBtn}
        </button>

        <p className="text-center text-[13px] text-[#86868b] mt-8">
          {t.auth.noAccount}{" "}
          <Link to="/register" className="text-[#1d1d1f] hover:underline" data-testid="link-to-register">
            {t.auth.registerBtn}
          </Link>
        </p>
      </div>
    </main>
  );
}
